import { Routes, Route } from 'react-router-dom'
import { useEffect, Suspense, lazy } from 'react'
import { Toaster } from '@/components/ui/toaster'
import Layout from '@/components/Layout'
import ScrollToTop from '@/components/ScrollToTop'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useGoogleAnalytics, initializeGA } from '@/hooks/useGoogleAnalytics'

// Lazy load all pages for code splitting
const Home = lazy(() => import('@/pages/Home'))
const Courses = lazy(() => import('@/pages/Courses'))
const About = lazy(() => import('@/pages/About'))
const Contact = lazy(() => import('@/pages/Contact'))
const CourseDetails = lazy(() => import('@/pages/CourseDetails'))
const SuccessStories = lazy(() => import('@/pages/SuccessStories'))
const HireFromUs = lazy(() => import('@/pages/HireFromUs'))
const BeAMentor = lazy(() => import('@/pages/BeAMentor'))
const RefundPolicy = lazy(() => import('@/app/refund/page'))
const RefundPolicyAlt = lazy(() => import('@/app/refund-policy/page'))
const TermsConditions = lazy(() => import('@/app/terms/page'))
const PrivacyPolicy = lazy(() => import('@/app/privacy/page'))
const Sitemap = lazy(() => import('@/pages/Sitemap'))
const Locations = lazy(() => import('@/pages/Locations'))
const LocationPage = lazy(() => import('@/pages/LocationPage'))
const Franchise = lazy(() => import('@/pages/Franchise'))
const NotFound = lazy(() => import('@/pages/NotFound'))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

function App() {
  // Initialize Google Analytics
  useEffect(() => {
    initializeGA();
  }, []);

  // Use Google Analytics hook for tracking
  useGoogleAnalytics();
  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetails />} />
              <Route path="/aida-cloud" element={<CourseDetails />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/training/:locationId" element={<LocationPage />} />
              {/* Legacy route for backward compatibility */}
              <Route path="/training/:location" element={<Locations />} />
              <Route path="/success-stories" element={<SuccessStories />} />
              <Route path="/hire-from-us" element={<HireFromUs />} />
              <Route path="/be-a-mentor" element={<BeAMentor />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/franchise" element={<Franchise />} />
              <Route path="/partner-with-us" element={<Franchise />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicyAlt />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/sitemap" element={<Sitemap />} />
              {/* 404 Catch-all - Must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
        <Toaster />
      </div>
    </ErrorBoundary>
  )
}

export default App 